/**
 * Static UI enumerations.
 *
 * These arrived on the legacy `getAllData` blob — the Yii2 session response
 * shipped the fifty US states, three phone types and two genders on every
 * sign-in, alongside the patient's chart. They are not session data and they
 * are not clinical vocabulary (which lives behind `/api/v1/vocabularies/*`);
 * they are fixed lists a form needs to render its `<select>`, so they belong
 * with the form.
 *
 * The `id` / `name` / `type` / `value` key names are the shapes the existing
 * templates already bind against, kept as-is so this is a relocation rather
 * than a rewrite of every form.
 */

/**
 * `id` is the two-letter code the API stores and validates (`size:2`). Each
 * also carries it as `abbr`, the key main's contact and add-user forms read
 * for the code (they bound a numeric id and the abbreviation beside it).
 */
const STATE_LIST = [
  { id: 'AL', name: 'Alabama' },      { id: 'AK', name: 'Alaska' },
  { id: 'AZ', name: 'Arizona' },      { id: 'AR', name: 'Arkansas' },
  { id: 'CA', name: 'California' },   { id: 'CO', name: 'Colorado' },
  { id: 'CT', name: 'Connecticut' },  { id: 'DE', name: 'Delaware' },
  { id: 'DC', name: 'District of Columbia' },
  { id: 'FL', name: 'Florida' },      { id: 'GA', name: 'Georgia' },
  { id: 'HI', name: 'Hawaii' },       { id: 'ID', name: 'Idaho' },
  { id: 'IL', name: 'Illinois' },     { id: 'IN', name: 'Indiana' },
  { id: 'IA', name: 'Iowa' },         { id: 'KS', name: 'Kansas' },
  { id: 'KY', name: 'Kentucky' },     { id: 'LA', name: 'Louisiana' },
  { id: 'ME', name: 'Maine' },        { id: 'MD', name: 'Maryland' },
  { id: 'MA', name: 'Massachusetts' },{ id: 'MI', name: 'Michigan' },
  { id: 'MN', name: 'Minnesota' },    { id: 'MS', name: 'Mississippi' },
  { id: 'MO', name: 'Missouri' },     { id: 'MT', name: 'Montana' },
  { id: 'NE', name: 'Nebraska' },     { id: 'NV', name: 'Nevada' },
  { id: 'NH', name: 'New Hampshire' },{ id: 'NJ', name: 'New Jersey' },
  { id: 'NM', name: 'New Mexico' },   { id: 'NY', name: 'New York' },
  { id: 'NC', name: 'North Carolina' },{ id: 'ND', name: 'North Dakota' },
  { id: 'OH', name: 'Ohio' },         { id: 'OK', name: 'Oklahoma' },
  { id: 'OR', name: 'Oregon' },       { id: 'PA', name: 'Pennsylvania' },
  { id: 'RI', name: 'Rhode Island' }, { id: 'SC', name: 'South Carolina' },
  { id: 'SD', name: 'South Dakota' }, { id: 'TN', name: 'Tennessee' },
  { id: 'TX', name: 'Texas' },        { id: 'UT', name: 'Utah' },
  { id: 'VT', name: 'Vermont' },      { id: 'VA', name: 'Virginia' },
  { id: 'WA', name: 'Washington' },   { id: 'WV', name: 'West Virginia' },
  { id: 'WI', name: 'Wisconsin' },    { id: 'WY', name: 'Wyoming' },
  { id: 'PR', name: 'Puerto Rico' },  { id: 'VI', name: 'U.S. Virgin Islands' },
  { id: 'GU', name: 'Guam' },         { id: 'AS', name: 'American Samoa' },
  { id: 'MP', name: 'Northern Mariana Islands' },
];

export const STATES = STATE_LIST.map((state) => ({ ...state, abbr: state.id }));

export const PHONE_TYPES = [
  { id: 'mobile', type: 'Mobile' },
  { id: 'home', type: 'Home' },
  { id: 'work', type: 'Work' },
];

/**
 * Matches `PatientContact::RELATIONSHIPS` on the server. A value the API will
 * reject is worse than one the form does not offer, so this list stays in step
 * with the enum the request rules validate against.
 *
 * The licensed types are main's: its contact form treats a contact of one as a
 * licensed individual (UserContact `isLicensed`, by these exact words) and
 * shows the licence and the Nursys check. Each entry also carries its words as
 * `value`, the key main's forms read a relation's label from.
 */
const RELATION_LIST = [
  { id: 'self', description: 'Self' },
  { id: 'spouse', description: 'Spouse' },
  { id: 'domestic_partner', description: 'Domestic partner' },
  { id: 'parent', description: 'Parent' },
  { id: 'child', description: 'Child' },
  { id: 'sibling', description: 'Sibling' },
  { id: 'caregiver', description: 'Caregiver' },
  { id: 'health_care_proxy', description: 'Health care proxy' },
  { id: 'friend', description: 'Friend' },
  { id: 'other_relative', description: 'Other relative' },
  { id: 'registered_nurse', description: 'Registered Nurse' },
  { id: 'licensed_practical_nurse', description: 'Licensed Practical Nurse' },
  { id: 'geriatric_care_manager', description: 'Geriatric Care Manager' },
  { id: 'physical_therapist', description: 'Physical Therapist' },
  { id: 'occupational_therapist', description: 'Occupational Therapist' },
  { id: 'dietician', description: 'Dietician' },
  { id: 'social_worker', description: 'Social Worker' },
  { id: 'speech_therapist', description: 'Speech Therapist' },
  { id: 'other', description: 'Other' },
];

export const RELATION_DESCRIPTIONS = RELATION_LIST.map((relation) => ({ ...relation, value: relation.description }));

export default { STATES, PHONE_TYPES, RELATION_DESCRIPTIONS };
